import { useCallback, useRef, useState } from 'react';
import { absolutize, ENDPOINTS, parseYouTube } from '../lib/api.js';
import { useCmsList } from '../hooks/useCmsList.js';
import { useVideoCrop } from '../hooks/useVideoCrop.js';
import { useReveal, reveal } from '../hooks/useReveal.js';
import { IN_ACTION } from '../content/home.js';
import Lightbox from '../components/ui/Lightbox.jsx';
import AccentedHeading from '../components/ui/Accent.jsx';

/**
 * Photos and video from real farms: sensor installs, walk-throughs, the system
 * in use.
 *
 * This is the strongest proof the site has. A dashboard screenshot argues that
 * the software is tidy; a photograph of a technician mounting a sensor in a
 * poultry house argues that the company exists, turns up, and has done this
 * before. For a buyer weighing a hardware install on their own farm, that is
 * the question actually being asked.
 *
 * Two CMS collections feed it — farm tours and IoT installs — merged into one
 * rail, because the split is an artefact of how the admin stores them and
 * means nothing to a visitor.
 */
export default function InAction() {
  const projects = useCmsList(ENDPOINTS.projects, { map: toMedia });
  const iot = useCmsList(ENDPOINTS.iotImages, { map: toMedia });

  const [ref, shown] = useReveal();
  const [active, setActive] = useState(null);

  const items = [...projects.items, ...iot.items];
  const loaded = projects.loaded && iot.loaded;

  // Nothing to show and nothing settled yet: render no section at all rather
  // than a heading with a hole under it.
  if (!items.length && !loaded) return null;

  const head = reveal(shown);

  return (
    <section id="in-action" className="scroll-mt-24 overflow-hidden bg-deep py-16 lg:py-20">
      <div ref={ref} className="wrap">
        <div className={`mb-9 max-w-[640px] ${head.className}`} style={head.style}>
          <h2 className="mb-3 font-display text-[24px] font-bold text-white lg:text-[30px]">
            <AccentedHeading text={IN_ACTION.heading} accent={IN_ACTION.accent} />
          </h2>
          <p className="text-[15px] leading-relaxed text-mist">{IN_ACTION.sub}</p>
        </div>
      </div>

      {items.length ? (
        <Rail items={items} shown={shown} onOpen={setActive} />
      ) : (
        <div className="wrap">
          <p className="rounded-2xl border border-white/10 px-6 py-8 text-center text-[14px] text-mist">
            {IN_ACTION.empty}
          </p>
        </div>
      )}

      {active && (
        <Lightbox
          kind={active.kind}
          src={active.kind === 'video' ? active.embedUrl : active.src}
          title={active.title}
          onClose={() => setActive(null)}
        />
      )}
    </section>
  );
}

/**
 * Horizontal rail.
 *
 * CSS scroll-snap rather than a carousel library. On a phone this is the
 * platform's own scrolling — real momentum, real rubber-banding, and it
 * responds instantly because no JavaScript sits between the finger and the
 * pixels. A JS carousel has to reimplement all of that and usually gets it
 * slightly wrong. The arrows are progressive enhancement for mouse users, who
 * have no thumb to swipe with; with JS broken the rail still scrolls.
 */
function Rail({ items, shown, onOpen }) {
  const trackRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const sync = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    // The 4px slack absorbs sub-pixel rounding, which otherwise leaves the end
    // arrow enabled on a rail that cannot scroll any further.
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    // Roughly one card, so a click advances by a readable amount rather than a
    // fixed pixel count that means different things at different widths.
    el.scrollBy({ left: dir * Math.max(280, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <ul
        ref={trackRef}
        onScroll={sync}
        // scroll-padding must match the inline padding. Without it, mandatory
        // snapping aligns the first card to the raw container edge and parks
        // the rail at scrollLeft equal to the padding — so it can never reach
        // 0, and the "at start" arrow state is wrong from the first paint.
        className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto px-[max(1rem,calc((100vw-1180px)/2))] pb-2 scroll-pl-[max(1rem,calc((100vw-1180px)/2))]"
      >
        {items.map((item, i) => {
          // Capped so a long rail does not end up with a second-long wait on
          // the last card.
          const r = reveal(shown, Math.min(i, 5) * 70);
          return (
            <li
              key={item.id}
              className={`w-[260px] flex-none snap-start sm:w-[300px] ${r.className}`}
              style={r.style}
            >
              <Card item={item} onOpen={onOpen} />
            </li>
          );
        })}
      </ul>

      <Arrow dir={-1} disabled={atStart} onClick={() => nudge(-1)} />
      <Arrow dir={1} disabled={atEnd} onClick={() => nudge(1)} />
    </div>
  );
}

function Arrow({ dir, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir < 0 ? 'Scroll left' : 'Scroll right'}
      className={`absolute top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-deep shadow-float transition hover:bg-amber disabled:pointer-events-none disabled:opacity-0 lg:flex ${
        dir < 0 ? 'left-4' : 'right-4'
      }`}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {dir < 0 ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
      </svg>
    </button>
  );
}

/** Cards are 4:5. */
const CARD_ASPECT = 4 / 5;

function Card({ item, onOpen }) {
  const isVideo = item.kind === 'video';

  /*
   * Farm clips are shot upright and exported into a 16:9 file, so the
   * thumbnail carries black bars baked in. object-cover on a 4:5 card crops a
   * window wider than the picture, leaving those bars showing down both sides.
   * Zooming by the ratio of the two shapes makes the picture fill the card
   * exactly. Photos and correctly framed video detect as 1 and are untouched.
   */
  const aspect = useVideoCrop(isVideo ? item.poster : null, item.posterFallback);
  const zoom = isVideo && aspect < CARD_ASPECT ? Math.min(CARD_ASPECT / aspect, 2.5) : 1;

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      aria-label={isVideo ? `Play video: ${item.title}` : `View photo: ${item.title}`}
      className="group relative block aspect-[4/5] w-full overflow-hidden rounded-2xl bg-field text-left"
    >
      {/* The hover lift lives on the wrapper so it cannot fight the inline
          zoom transform on the image itself. */}
      <span className="absolute inset-0 block overflow-hidden transition duration-500 group-hover:scale-105">
        <img
          src={item.poster}
          alt=""
          loading="lazy"
          decoding="async"
          style={zoom !== 1 ? { transform: `scale(${zoom})` } : undefined}
          className="h-full w-full object-cover"
          onError={(e) => {
            if (item.posterFallback && e.currentTarget.src !== item.posterFallback) {
              e.currentTarget.src = item.posterFallback;
            } else {
              // A CMS row pointing at a dev host would otherwise leave a
              // broken-image card sitting in the rail.
              e.currentTarget.closest('li')?.remove();
            }
          }}
        />
      </span>
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-deep via-deep/25 to-transparent"
      />

      {isVideo && (
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-amber shadow-float transition duration-300 group-hover:scale-110"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#122E1B" className="ml-1">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      )}

      <span className="absolute inset-x-4 bottom-4">
        <b className="block font-display text-[15px] font-bold capitalize text-white">
          {item.title}
        </b>
        <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.12em] text-mist">
          {isVideo ? 'Watch' : item.category || 'Photo'}
        </span>
      </span>
    </button>
  );
}

/**
 * Normalises the two collections into one shape.
 *
 * The project and IoT tables name their columns differently for what is the
 * same thing, so the difference is flattened here rather than leaking into the
 * components.
 */
function toMedia(row) {
  const title = row.project_name ?? row.title ?? '';
  const rawImage = row.project_image ?? row.image;
  const rawVideo = row.project_video ?? row.video_url;

  if (rawVideo) {
    const yt = parseYouTube(rawVideo);
    if (!yt) return null;
    return {
      id: `v-${row.id}-${title}`,
      kind: 'video',
      title,
      category: row.category?.project_category ?? null,
      embedUrl: yt.embedUrl,
      poster: yt.posterUrl,
      posterFallback: yt.posterFallbackUrl,
    };
  }

  const src = absolutize(rawImage);
  if (!src) return null;

  return {
    id: `i-${row.id}-${title}`,
    kind: 'image',
    title,
    category: row.category?.project_category ?? null,
    src,
    poster: src,
    posterFallback: null,
  };
}
